// Small widget parts for the orb rows. Sizes scale with the orb (k = size / 300).
import React, { createContext, useContext, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { useV10, DISPLAY_FONT } from '../theme';

export const OrbScale = createContext(1);
export const useK = () => useContext(OrbScale);

type Tone = 'gold' | 'ghost' | 'ok' | 'warn';

export function Btn({ label, onPress, tone = 'ghost', disabled, style }: { label: string; onPress: () => void; tone?: Tone; disabled?: boolean; style?: StyleProp<ViewStyle> }) {
  const t = useV10();
  const k = useK();
  const solid = tone === 'gold' ? t.gold : tone === 'ok' ? t.onPace : tone === 'warn' ? t.behind : 'transparent';
  const edge = tone === 'ghost' ? t.gold : solid;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      hitSlop={4}
      style={[
        { height: 27 * k, paddingHorizontal: 11 * k, borderRadius: 14 * k, borderWidth: 1, borderColor: tone === 'ghost' ? 'rgba(214,180,106,0.55)' : edge, backgroundColor: solid, alignItems: 'center', justifyContent: 'center', opacity: disabled ? 0.45 : 1 },
        { outlineStyle: 'none' } as object,
        style,
      ]}
    >
      <Text style={{ color: tone === 'ghost' ? t.ivorySoft : t.onGold, fontSize: 12 * k, fontFamily: DISPLAY_FONT }} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

/** ✓ / ○ marker: green when ready, amber when not. */
export function Mark({ ok }: { ok: boolean }) {
  const t = useV10();
  const k = useK();
  return <Text style={{ color: ok ? t.onPace : t.behind, fontSize: 12 * k, fontWeight: '900', width: 14 * k, textAlign: 'center' }}>{ok ? '✓' : '○'}</Text>;
}

export function Txt({ children, style, lines, soft, faint, w }: { children: React.ReactNode; style?: StyleProp<TextStyle>; lines?: number; soft?: boolean; faint?: boolean; w?: number }) {
  const t = useV10();
  const k = useK();
  return (
    <Text numberOfLines={w ? 1 : lines} style={[{ color: faint ? t.ivoryFaint : soft ? t.ivorySoft : t.ivory, fontSize: 12 * k, lineHeight: 16 * k }, w ? { width: w * k } : null, style]}>
      {children}
    </Text>
  );
}

export function Big({ children }: { children: React.ReactNode }) {
  const t = useV10();
  const k = useK();
  return <Text style={{ color: '#f5efe0', fontSize: 30 * k, lineHeight: 34 * k, fontFamily: DISPLAY_FONT, textAlign: 'center' }}>{children}</Text>;
}

export function Quiet({ children }: { children: React.ReactNode }) {
  const t = useV10();
  const k = useK();
  return <Text style={{ color: t.ivoryFaint, fontStyle: 'italic', fontFamily: DISPLAY_FONT, textAlign: 'center', fontSize: 12 * k, lineHeight: 16 * k }}>{children}</Text>;
}

export function Head({ children }: { children: React.ReactNode }) {
  const t = useV10();
  const k = useK();
  return <Text style={{ color: t.goldBright, fontSize: 15.5 * k, lineHeight: 20 * k, fontFamily: DISPLAY_FONT, textAlign: 'center', alignSelf: 'stretch' }}>{children}</Text>;
}

/** One list line: text on the left, small controls on the right. */
export function Line({ children, right, onPress }: { children: React.ReactNode; right?: React.ReactNode; onPress?: () => void }) {
  const k = useK();
  const inner = (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 * k, minHeight: 22 * k, alignSelf: 'stretch' }}>
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 2 }}>{children}</View>
      {right}
    </View>
  );
  return onPress ? (
    <Pressable onPress={onPress} style={{ alignSelf: 'stretch' }}>
      {inner}
    </Pressable>
  ) : (
    inner
  );
}

/** Text field: white fill + visible border so it never blends into the orb. */
export function Field({ value, onChangeText, placeholder, multiline, onSubmit }: { value: string; onChangeText: (s: string) => void; placeholder?: string; multiline?: boolean; onSubmit?: () => void }) {
  const t = useV10();
  const k = useK();
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#7a8194"
      multiline={multiline}
      onSubmitEditing={multiline ? undefined : onSubmit}
      returnKeyType="done"
      style={[
        { alignSelf: 'stretch', borderRadius: 8 * k, borderWidth: 1, borderColor: t.gold, backgroundColor: '#ffffff', color: '#10162a', paddingHorizontal: 8 * k, fontSize: 12 * k },
        multiline ? { minHeight: 44 * k, paddingVertical: 4 * k, textAlignVertical: 'top' } : { height: 26 * k, paddingVertical: 0 },
        { outlineStyle: 'none' } as object,
      ]}
    />
  );
}

/** A one-line prompt + save (CLIO lessons). */
export function PromptLine({ prompt, saved, onSave }: { prompt: string; saved?: string; onSave: (text: string) => Promise<boolean> }) {
  const k = useK();
  const [v, setV] = useState('');
  const [ok, setOk] = useState(false);
  return (
    <View style={{ alignSelf: 'stretch', gap: 3 * k }}>
      <Txt faint style={{ fontStyle: 'italic' }}>{prompt}</Txt>
      {saved && !v ? <Txt soft lines={2}>“{saved}”</Txt> : null}
      <View style={{ flexDirection: 'row', gap: 5 * k, alignItems: 'center' }}>
        <View style={{ flex: 1 }}>
          <Field
            value={v}
            onChangeText={(s) => {
              setV(s);
              setOk(false);
            }}
            placeholder="One line"
            onSubmit={() => void onSave(v).then((r) => r && (setV(''), setOk(true)))}
          />
        </View>
        <Btn label={ok ? 'Saved ✓' : 'Save'} tone="gold" disabled={!v.trim()} onPress={() => void onSave(v).then((r) => r && (setV(''), setOk(true)))} />
      </View>
    </View>
  );
}

/** Done / Not done + reason, with METIS's configured reasons as quick picks. */
export function DoneOrNot({ completed, missed, reasons, onDone, onNot, canNot = true }: { completed: boolean; missed: string; reasons: string[]; onDone: () => void; onNot: (reason: string) => Promise<boolean> | void; canNot?: boolean }) {
  const t = useV10();
  const k = useK();
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState('');
  const submit = (r: string) => {
    const v = r.trim();
    if (!v) return;
    void Promise.resolve(onNot(v)).then(() => {
      setOpen(false);
      setWhy('');
    });
  };
  return (
    <View style={{ alignSelf: 'stretch', gap: 5 * k, alignItems: 'center' }}>
      <View style={{ flexDirection: 'row', gap: 6 * k }}>
        <Btn label={completed ? 'Done ✓' : 'Done'} tone={completed ? 'ok' : 'gold'} onPress={onDone} />
        {canNot ? <Btn label={missed ? 'Not done ✓' : 'Not done'} tone={missed ? 'warn' : 'ghost'} onPress={() => setOpen((o) => !o)} /> : null}
      </View>
      {missed && !open ? <Txt soft lines={2} style={{ textAlign: 'center' }}>Not done: {missed}</Txt> : null}
      {open ? (
        <View style={{ alignSelf: 'stretch', gap: 4 * k }}>
          {reasons.length ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 * k, justifyContent: 'center' }}>
              {reasons.slice(0, 4).map((r) => (
                <Pressable key={r} onPress={() => submit(r)} style={{ paddingHorizontal: 7 * k, height: 20 * k, borderRadius: 10 * k, borderWidth: 1, borderColor: t.gold, justifyContent: 'center' }}>
                  <Text style={{ color: t.ivory, fontSize: 10 * k, fontWeight: '700' }} numberOfLines={1}>{r}</Text>
                </Pressable>
              ))}
            </View>
          ) : null}
          <View style={{ flexDirection: 'row', gap: 5 * k, alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <Field value={why} onChangeText={setWhy} placeholder="Reason" onSubmit={() => submit(why)} />
            </View>
            <Btn label="Save" tone="gold" disabled={!why.trim()} onPress={() => submit(why)} />
          </View>
        </View>
      ) : null}
    </View>
  );
}

export const rowStyles = StyleSheet.create({
  col: { alignSelf: 'stretch', gap: 4 },
});
